/**
 * LazyLoader — defers execution of heavy functions until user interaction,
 * idle time, or timeout, and waits for DOMContentLoaded first.
 */
export class LazyLoader {
    /**
     *  Creates a LazyLoader instance.
     *
     * @param {Function} fn - The function to execute lazily.
     * @param {object} options - Configuration options.
     * @param {number} [options.delay=2000] - Delay after trigger in ms.
     * @param {boolean} [options.idle=true] - Use requestIdleCallback if supported.
     * @param {string[]} [options.events=["scroll","mousemove","touchstart","keydown","pointerdown"]] - User interaction events to trigger lazy load.
     */
    constructor(fn, options = {}) {
        this.fn = fn;
        this.hasRun = false;

        const {
            delay = 2000,
            idle = true,
            events = [
                "scroll",
                "mousemove",
                "touchstart",
                "keydown",
                "pointerdown",
            ],
        } = options;

        this.delay = delay;
        this.idle = idle && "requestIdleCallback" in window;
        this.events = events;
        this.timeoutId = null;

        this.init();
    }

    /**
     * Execute the lazy function once.
     */
    run() {
        if (this.hasRun) return;
        this.hasRun = true;
        this.fn();
        this.cleanup();
    }

    /**
     * Trigger the lazy function after delay or idle.
     */
    trigger() {
        if (this.hasRun || this.timeoutId !== null) return;

        if (this.idle) {
            // Prefer requestIdleCallback with timeout as safety
            window.requestIdleCallback(() => this.run(), {
                timeout: this.delay,
            });
            this.timeoutId = true; // mark as scheduled
        } else {
            this.timeoutId = setTimeout(() => this.run(), this.delay);
        }
    }

    /**
     * Attach event listeners for user interactions.
     */
    setupEventTriggers() {
        const triggerOnce = () => this.trigger();

        this.events.forEach((event) => {
            window.addEventListener(event, triggerOnce, {
                once: true,
                passive: true,
            });
        });
    }

    /**
     * Cleanup any pending timeout (requestIdleCallback cannot be canceled easily)
     */
    cleanup() {
        if (typeof this.timeoutId === "number") {
            clearTimeout(this.timeoutId);
        }

        this.timeoutId = null;
    }

    /**
     * Initialize the lazy loader.
     */
    init() {
        // Trigger on DOMContentLoaded if not yet ready
        if (document.readyState === "loading") {
            document.addEventListener(
                "DOMContentLoaded",
                () => this.trigger(),
                { once: true },
            );
        } else {
            this.trigger();
        }

        // Set up user interaction triggers
        this.setupEventTriggers();
    }
}

window.LazyLoader = LazyLoader;
